<script setup>
    import { onMounted, computed } from 'vue';
    import { useRoute } from 'vue-router';
    import { useHead } from '@vueuse/head';
    import Foot from '../components/Foot';
    import HeadBar from '../components/HeadBar';
    import { imageUrl } from '../composables/http';
    import useBlog from '../composables/useBlog';
    import { isNullOrEmpty, toDateFormat } from '../composables/utils';

    const route = useRoute();
    const id = route.params.id;
    const { loading, form, list, info, total, pages, readPage, onPageChange, onSearchChanged, onSearchClear } = useBlog();

    useHead({
        title: 'Contractly - Online Contracts for Canadian Tradesperson Contractors',
        meta: [
            { name: 'description', content: 'Create and manage professional contracts online. Contractly helps Canadian tradesperson builder contractors and sub-contractors formalize agreements quickly and build lasting relationships.' },
            { name: 'keywords', content: 'Canadian contractor contracts, online contract tool Canada, construction agreement generator, digital contractor agreements, subcontractor legal forms, tradesperson contract app, e-signature contracts Canada' },

            // Open Graph
            { property: 'og:title', content: 'Contractly - Online Contracts for Canadian Tradesperson Contractors' },
            { property: 'og:description', content: 'Create professional contracts in minutes. Simplified contract management for construction professionals.' },
            { property: 'og:url', content: 'https://contractly.ca' },
            { property: 'og:type', content: 'website' },
            { property: 'og:image', content: 'https://contractly.ca/images/social-preview.jpg' },
            { property: 'og:locale', content: 'en_CA' },

            // Twitter Card
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:title', content: 'Contractly - Professional Online Contracts for Canadian Tradesperson Contractors' },
            { name: 'twitter:description', content: 'Create and manage legally-binding contracts online. Perfect for Canadian contractors and sub-contractors.' }
        ],
        link: [
            { rel: 'canonical', href: window.location.href },
            { rel: 'icon', href: '/favicon.ico', type: 'image/x-icon' },
            { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' }
        ]
    });

    onMounted(() => {
        readPage();
    });
</script>

<template>
    <div class="home">
        <HeadBar />
        <div class="container mt-12 blog blog-list">
            <div v-if="list!=null" class="max-w-screen-xl mx-auto">
                <div class="mx-auto text-center font-bold text-xl md:text-3xl">
                    <div class="row">
                        <div class="col-0 col-md-4">&nbsp;</div>
                        <div class="col-2 col-md-4">Blog</div>
                        <div class="col-10 col-md-4">
                            <n-input-group>
                                <n-input style="width:90%; text-align:left;" v-model:value="form.searchWords" placeholder="" clearable @clear="onSearchClear" @keyup="onSearchChanged"></n-input>
                                <n-button type="primary" ghost @click="onSearchChanged">
                                    Search
                                </n-button>
                            </n-input-group>
                        </div>
                    </div>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-10 mt-6">
                    <template v-if="loading">
                        <n-card v-for="n in 12" :key="n" class="shadow-sm cursor-pointer hover:bg-slate-50 p-0" size="small" hoverable>
                            <n-skeleton text class="mt-1 w-1/2" />
                            <n-skeleton class="mt-2 h-[160px]" />
                            <n-skeleton text :repeat="2" class="mt-2" />
                            <n-skeleton text class="w-1/2" />
                        </n-card>
                    </template>

                    <router-link v-for="obj in list" :to="`/blog/${obj.Id}`" :key="`blog-${obj.Id}`">
                        <n-card class="shadow-sm cursor-pointer hover:bg-slate-50 p-0" size="small" hoverable>
                            <div class="overflow-hidden h-[160px] w-full mt-2">
                                <img :src="imageUrl(obj.Photo,560,0,'','max')"
                                     class="object-cover w-full h-full"
                                     style="border-radius: 4px;"
                                     :alt="obj.Title"
                                     loading="lazy">
                            </div>
                            <div class="mt-2" style="display: flex; gap: 4px;">
                                <div class="" style="flex: 1; font-size: 12px;">{{obj.Title}}</div>
                                <div class="bold" style="font-size: 12px;">{{toDateFormat(obj.CreatedTime,'MMM DD, YYYY')}}</div>
                            </div>
                        </n-card>
                    </router-link>
                </div>

                <n-pagination v-if="list!=null"
                              v-model:page="form.pageIndex"
                              :page-count="pages"
                              class="flex justify-end !mt-8 mb-8"
                              @update:page="onPageChange" />
            </div>
        </div>
        <Foot />
        <CommonInfo ref="terms"></CommonInfo>
    </div>
</template>

<style>
    .blog-list a {
        text-decoration: none;
        color: inherit;
        display:block;
    }
</style>